import { useEffect, useMemo, useState } from 'react';
import { AES } from 'crypto-js';
import type { PopoverProps } from '@mui/material';
import { Typography } from '@mui/material';

import { useCurrentAccount } from 'src/hooks/useAccount';
import { mnemonicToPair } from 'src/utils/crypto';

import {
  Base02TextContainer,
  CheckButton,
  CheckButtonContainer,
  CheckButtonIconContainer,
  CheckInstructionButton,
  Container,
  DescriptionContainer,
  EncrpyionKeyContainer,
  IconContainer,
  QrCodeWrapper,
  SeedPhraseContainer,
  SeedPhraseTopContainer,
  StyledPopover,
  TopContainer,
  TopLeftContainer,
  ViewIconContainer,
  WarningContainer,
  WarningDescriptionContainer,
  WarningIconContainer,
  WrapperContainer,
} from './styled';
import GradientButton from '../GradientButton';
import IconButton from '../IconButton';
import QrCode from '../QrCode';

import { ReactComponent as CheckIcon } from 'src/assets/svg/Check.svg';
import { ReactComponent as Close32Icon } from 'src/assets/svg/Close32.svg';
import { ReactComponent as MnemonicWarningIcon } from 'src/assets/svg/MnemonicWarning.svg';
import { ReactComponent as ViewIcon } from 'src/assets/svg/View28.svg';
import { ReactComponent as ViewBlockIcon } from 'src/assets/svg/ViewBlock.svg';

type MobileMnemonicPopoverProps = Omit<PopoverProps, 'children'>;

export default function MobileMnemonicPopover({ onClose, ...remainder }: MobileMnemonicPopoverProps) {
  const [isCheckedInstruction, setIsCheckedInstruction] = useState(false);
  const [isExport, setIsExport] = useState(false);
  const [isView, setIsView] = useState(false);

  const [isOpenTooltip, setIsOpenTooltip] = useState(false);

  const { currentAccount } = useCurrentAccount();

  const data = useMemo(
    () => ({
      mnemonic: currentAccount.mnemonic,
      cosmosAddress: currentAccount.cosmosAddress,
      pubkeyHex: Buffer.from(mnemonicToPair(currentAccount.mnemonic).publicKey).toString('hex'),
      privkeyHex: Buffer.from(mnemonicToPair(currentAccount.mnemonic).privateKey).toString('hex'),
    }),
    [currentAccount.cosmosAddress, currentAccount.mnemonic],
  );

  const encryptionKey = useMemo(() => String(Math.floor(100000 + Math.random() * 900000)), []);

  const encryptedData = useMemo(() => AES.encrypt(JSON.stringify(data), encryptionKey).toString(), [data, encryptionKey]);

  useEffect(() => {
    if (isOpenTooltip) {
      setTimeout(() => {
        setIsOpenTooltip(false);
      }, 1000);
    }
  }, [isOpenTooltip]);

  return (
    <WrapperContainer data-is-open={remainder.open}>
      <StyledPopover
        onClose={() => {
          onClose?.({}, 'backdropClick');

          setIsCheckedInstruction(false);
          setIsExport(false);
          setIsView(false);
        }}
        {...remainder}
      >
        <Container>
          <TopContainer>
            <TopLeftContainer>
              <Typography variant="h2">{isExport ? 'Export Mnemonic' : 'Reveal Mnemonic QR'}</Typography>
            </TopLeftContainer>
            <IconButton
              onClick={() => {
                onClose?.({}, 'backdropClick');

                setIsCheckedInstruction(false);
                setIsExport(false);
                setIsView(false);
              }}
            >
              <IconContainer>
                <Close32Icon />
              </IconContainer>
            </IconButton>
          </TopContainer>
          <DescriptionContainer>You are about transfer your keys via QR Code to the mobile app.</DescriptionContainer>
          <WarningDescriptionContainer>Never share this code with anyone. dYdX will never ask you for this.</WarningDescriptionContainer>

          {isExport ? (
            <SeedPhraseContainer>
              <SeedPhraseTopContainer>
                <EncrpyionKeyContainer>
                  <Base02TextContainer>
                    <Typography variant="h6">Encryption Key :</Typography>
                  </Base02TextContainer>
                  &nbsp;
                  <Typography variant="h6">{encryptionKey}</Typography>
                </EncrpyionKeyContainer>
                <IconButton
                  onClick={() => {
                    setIsView(!isView);
                  }}
                >
                  <ViewIconContainer>{isView ? <ViewIcon /> : <ViewBlockIcon />}</ViewIconContainer>
                </IconButton>
              </SeedPhraseTopContainer>

              <QrCodeWrapper data-is-view={isView}>
                <QrCode value={encryptedData} size={400} />
              </QrCodeWrapper>
            </SeedPhraseContainer>
          ) : (
            <>
              <WarningContainer>
                <WarningIconContainer>
                  <MnemonicWarningIcon />
                </WarningIconContainer>
                <Typography variant="h4">Anyone with your mnemonic has access to your wallet, putting your assets at risk.</Typography>
              </WarningContainer>

              {/* <CheckInstructionContainer>
                <CheckButtonContainer>
                  <CheckButton
                    onClick={() => {
                      setIsCheckedInstruction(!isCheckedInstruction);
                    }}
                  >
                    {isCheckedInstruction ? (
                      <CheckButtonIconContainer>
                        <CheckIcon />
                      </CheckButtonIconContainer>
                    ) : null}
                  </CheckButton>
                </CheckButtonContainer>
                <Typography variant="h7">I understand the risks and I will never share my mnemonic with anyone.</Typography>
              </CheckInstructionContainer> */}

              <CheckInstructionButton
                onClick={() => {
                  setIsCheckedInstruction(!isCheckedInstruction);
                }}
              >
                <CheckButtonContainer>
                  <CheckButton>
                    {isCheckedInstruction ? (
                      <CheckButtonIconContainer>
                        <CheckIcon />
                      </CheckButtonIconContainer>
                    ) : null}
                  </CheckButton>
                </CheckButtonContainer>
                <Typography variant="h7">I understand the risks and I will never share my mnemonic with anyone.</Typography>
              </CheckInstructionButton>
            </>
          )}
          {!isExport && (
            <GradientButton
              disabled={!isCheckedInstruction}
              onClick={() => {
                setIsExport(!isExport);
              }}
            >
              <Typography variant="h4">REVEAL MNEMONIC QR</Typography>
            </GradientButton>
          )}
        </Container>
      </StyledPopover>
    </WrapperContainer>
  );
}
